import { useQuery } from '@tanstack/vue-query'
import type { PaginationState } from '@tanstack/vue-table'
import type {
  QueryParamsByPath,
  ResponseByPath,
  SchemaAttributes,
  Schemas,
} from '@/api/types.js'
import { isDateWithinNext } from '@/utils/date/validation.js'
import type { Duration } from '@/utils/date/conversion.js'
import { useApi } from '@/composables/useApi.js'
import { useAuth } from '@/composables/useAuth.js'
import { useFeatureBlocks } from '@/composables/useFeatureBlocks.js'
import {
  createRelationshipBuilder,
  many,
  one,
  toPaginationQueryParams,
  type RelationshipDefs,
} from '@/utils/jsonapi.js'
const TASKS_URL = '/{provet_id}/api/tasks'
export type TaskQueryParams = QueryParamsByPath<'get', typeof TASKS_URL>
export type TaskListResponse = ResponseByPath<'get', typeof TASKS_URL>

const MY_TASKS_URL = '/{provet_id}/api/tasks/my_tasks'
export type MyTaskQueryParams = QueryParamsByPath<'get', typeof MY_TASKS_URL>
export type MyTaskListResponse = ResponseByPath<'get', typeof MY_TASKS_URL>

export type Task = Schemas['Task']
export type TaskAttributes = SchemaAttributes<'Task'>
export type TaskStatus = NonNullable<TaskAttributes['status']>
export type TaskType = NonNullable<TaskAttributes['task_type']>
export type TaskReminder = NonNullable<TaskAttributes['remind']>

export enum TaskStatusEnum {
  StatusNotDone = 'STATUS_NOT_DONE',
  StatusDone = 'STATUS_DONE',
}

const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (query: MaybeRef<TaskQueryParams>) =>
    [...taskKeys.lists(), query] as const,
}

const myTaskKeys = {
  all: [...taskKeys.all, 'my_tasks'] as const,
  lists: () => [...myTaskKeys.all, 'list'] as const,
  list: (query: MaybeRef<TaskQueryParams>) =>
    [...myTaskKeys.lists(), query] as const,
}

export function useTasksPermission() {
  const { getPermission } = useAuth()
  const { visibleFeatureBlocks } = useFeatureBlocks()
  const blockVisibility = computed(() =>
    visibleFeatureBlocks.value.includes('task'),
  )
  const permission = computed(() => getPermission('task'))

  const read = computed(() => permission.value.read && blockVisibility.value)
  const write = computed(() => permission.value.write && blockVisibility.value)

  return [read, write]
}

// params that are shared between endpoints
const tasksQueryParams: TaskQueryParams = {
  include: ['patients', 'client'],
  'filter[status]': [TaskStatusEnum.StatusNotDone],
  fields: {
    Task: [
      'title',
      'status',
      'due',
      'remind',
      'favourite',
      'legacy_url',
      'task_repeat',
      'client',
      'patients',
    ],
    Patient: ['name'],
    Client: ['first_name', 'last_name'],
    User: ['initials'],
  },
} as const

export function useMyTasksQuery(
  pagination: MaybeRef<Partial<PaginationState>>,
  refetchInterval: number | false = false,
) {
  const api = useApi()
  const route = useRoute()
  const [read] = useTasksPermission()

  const queryParams = computed(
    () =>
      ({
        ...toPaginationQueryParams(pagination),
        ...tasksQueryParams,
        include: [...tasksQueryParams.include!, 'user'],
      }) satisfies MyTaskQueryParams,
  )

  const query = useQuery({
    queryKey: myTaskKeys.list(queryParams),
    enabled: read,
    refetchInterval,
    queryFn: async () =>
      api((client) =>
        client.GET(MY_TASKS_URL, {
          params: {
            path: { provet_id: route.params.provetId },
            query: queryParams.value,
          },
        }),
      ),
  })

  return reactive(query)
}

export function useTasksQuery(
  pagination: MaybeRef<Partial<PaginationState>>,
  refetchInterval: number | false = false,
) {
  const api = useApi()
  const route = useRoute()
  const [read] = useTasksPermission()
  const { user } = useAuth()

  const queryParams = computed(
    () =>
      ({
        ...toPaginationQueryParams(pagination),
        ...tasksQueryParams,
        'filter[user.id]': unref(user).id,
      }) satisfies TaskQueryParams,
  )

  const query = useQuery({
    queryKey: taskKeys.list(queryParams),
    enabled: read,
    refetchInterval,
    queryFn: async () =>
      api((client) =>
        client.GET(TASKS_URL, {
          params: {
            path: { provet_id: route.params.provetId },
            query: queryParams.value,
          },
        }),
      ),
  })

  return reactive(query)
}

const durations: Record<TaskReminder, Duration> = {
  REMINDER_0: { minutes: 0 },
  REMINDER_10: { minutes: 10 },
  REMINDER_30: { minutes: 30 },
  REMINDER_60: { minutes: 60 },
}

export type TaskDueStatus = 'past-due' | 'almost-due' | undefined

export function taskDueStatus(task: Task): TaskDueStatus {
  if (!task.attributes?.due) {
    return undefined
  }

  const dueDate = new Date(task.attributes.due)

  if (dueDate.getTime() < Date.now()) {
    return 'past-due'
  }

  if (
    task.attributes.remind &&
    isDateWithinNext(dueDate, durations[task.attributes.remind])
  ) {
    return 'almost-due'
  }
}

const relationships = {
  client: one('Client'),
  patients: many('Patient'),
  user: one('User'),
} satisfies RelationshipDefs<Schemas['Task']>

export const buildTaskRelationships = createRelationshipBuilder(relationships)
